import React, { useContext, useEffect, useState } from "react";
import { CloseIcon } from "./icon/close-x";
import { ArrowLeft } from "./icon/arrow-left";
import { cn, dualTypeTrue } from "../lib/helpers";
import { Button } from "./button";
import { SiteContext } from "./global/site-context";
import { Chatbot } from "./sections";
import { pageDocPath, trackPath } from "../../helpers";
import { PAGE_VIEWS_STASH, useScore } from "../lib/score";
import { FIRST_SEEN_STASH } from "../lib/client-attribution";
import { differenceInCalendarDays } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRobot } from "@fortawesome/free-solid-svg-icons";
import { graphql, useStaticQuery } from "gatsby";

const POPUP_DISMISS_STASH = "popup.dismissed";
export const HAS_SENT_CONTACT_STASH = "action.contactSend";
export const DELAY_FORM_SUBMIT = 2200;

const SCORE_THRESHOLD = 8;
const PAGEVIEW_THRESHOLD = 5;
const FIRST_SEEN_THRESHOLD = 7;

// @todo skip signup on all hello pages
// @todo this should not effect the dark nav mode but it does

const POPUP_EXCLUDE_PATHS = [
  "/contact/",
  "/demo/",
  "/pricing/",
  "/pricing/zra/",
  "/join-slack/",
  "/hello/",
  "/acquired/",
  "/meeting/1to1/",
  "/meeting/30mins/",
  "/unsubscribe-onboarding/",
  "/help/",
  "/devrel-github-list/",
  "/playbooks/",
  "/resources/",
  "/resources-new/",
  "/resources/signals/",
  "/community/",
  "/uncommon/",
  "/series/mavericks/",
  "/prompts/",
  "/ask/",
  "/start/",
  "/plays/",
  "/priority-express/",
  "/priority-express-new/",
  "/events/",
];

const agentQuery = graphql`
  query {
    gtmAgent: sanityAgent(_id: { eq: "9984d4fd-97ce-4692-abba-3f2f6729ffdd" }) {
      ...Agent
    }
  }
`;

// popup header includes HTML break
const POPUP_BUTTON = "Can we answer any questions?";
const FORM_BUTTON = "Request demo";
const FORM_HEADER = "See us in action";

const FORM_MODE = false;

export const StickyPopup = () => {
  const site = useContext(SiteContext);
  const { gtmAgent } = useStaticQuery(agentQuery) || {};

  // use page intent value to calculate a running total.
  const score = useScore({
    storage: site.metrics.analytics.storage,
    doc: site?.doc,
  });

  const localStorage = site.metrics.analytics.storage;
  const storageBackend =
    typeof window !== "undefined"
      ? window.sessionStorage || localStorage
      : null;

  const wasDismissed = dualTypeTrue(localStorage.getItem(POPUP_DISMISS_STASH));
  const isExcluded =
    localStorage && dualTypeTrue(localStorage.getItem(HAS_SENT_CONTACT_STASH));

  const pageViews =
    storageBackend && parseInt(storageBackend.getItem(PAGE_VIEWS_STASH));
  const firstSeen = site.metrics.analytics.storage.getItem(FIRST_SEEN_STASH);
  const daysSinceFirstSeen = differenceInCalendarDays(
    new Date(),
    new Date(firstSeen)
  );

  const showPopup =
    (score >= SCORE_THRESHOLD ||
      pageViews >= PAGEVIEW_THRESHOLD ||
      daysSinceFirstSeen >= FIRST_SEEN_THRESHOLD) &&
    includePromos(site.doc);

  // sticky popup wrapper
  const [isExpanded, setExpanded] = useState(!wasDismissed);
  // model with form
  const [isOpen, setOpen] = useState(false);
  // chatbot copilot
  const [copilotOpen, setCopilotOpen] = useState(false);

  useEffect(() => {
    if (isExcluded || wasDismissed || !showPopup) return;

    site.metrics.analytics.once("ready", () => {
      site.metrics.logEvent({
        category: "passive",
        action: "popup-chat",
        label: "view",
      });
    });
  }, []);

  return isExcluded || !showPopup ? null : (
    <div
      className={cn(
        "z-90 overflow-hidden bg-white fixed bottom-8 shadow-lg",
        isExpanded
          ? "left-8 sm:left-auto right-3 rounded-xl"
          : "right-0 p-4 rounded-tl-xl rounded-bl-xl cursor-pointer",

      )}
      onClick={(e) => {
        e.stopPropagation();
        if (!isExpanded) {
          setExpanded(true);
          localStorage.setItem(POPUP_DISMISS_STASH, false);
        }
      }}
      style={{
        boxShadow: "0 0 20px rgba(0, 0, 0, 0.1)",
        maxHeight: copilotOpen ? "75%" : null,
      }}
    >
      {isExpanded ? (
        <div className="relative">
          <div className="flex items-center justify-between pl-3">
            <span className="font-medium mt-1">
              <FontAwesomeIcon icon={faRobot} size="lg" className="max-h-8 text-auxiliary" />
            </span>
            <span
              className="p-3 cursor-pointer scale-100 hover:scale-125 transform transition-scale duration-200 hover:bg-dark-5 rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(false);
                localStorage.setItem(POPUP_DISMISS_STASH, true);
                site.metrics.logClick("popup-chat", "dismiss");
              }}
            >
              <CloseIcon size="18" />
            </span>
          </div>
          <div
            className="flex justify-center items-end"
            style={{
              width: copilotOpen ? "36rem" : "16rem",
              overflowY: copilotOpen ? "scroll" : "hidden",
            }}>
            {copilotOpen ? (
              <div
                className="px-2 w-full flex flex-col items-end overflow-y-scroll"
                style={{ maxHeight: "calc(100vh - 350px)" }}
              >
                <Chatbot agent={gtmAgent} minimal={true} />
              </div>
            ) : (
              <Button
                text={POPUP_BUTTON}
                bgColor="bg-white"
                noLink={true}
                isBig={true}
                className="px-6 text-2xl mt-6 mb-12"
                onClick={(e) => {
                  e.stopPropagation();
                  setCopilotOpen(true);
                  site.metrics.logClick("popup-chat", "open");
                }}
              />
            )}
          </div>
        </div>
      ) : (
        <div className=" scale-100 hover:scale-125 transform transition-scale duration-200">
          <ArrowLeft size={16} />
        </div>
      )
      }
    </div >
  );
};

export const includePromos = (doc) => {
  const path = pageDocPath(doc);

  // note reverse logic to exclude
  return [
    "documentation", "redirect", "category", "company", "agent", "signalSource"
  ].includes(doc._type) ||
    doc?.contentType === "landing" ||
    path.match(/^\/hello\//) ||
    POPUP_EXCLUDE_PATHS.includes(path)
    ? false
    : true;
};
